import { useEffect, useState, useMemo } from "react";
import {
  Form,
  Link,
  useLoaderData,
  useActionData,
  useNavigation,
  useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { PODCAST_CATEGORIES, PODCAST_LANGUAGES } from "../../schemas";
import { generateSlug } from "../../utils";
import type { CreatePodcastLoaderData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import styles from "./CreatePodcastPage.module.css";

// Common timezones
const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern Time (US)" },
  { value: "America/Chicago", label: "Central Time (US)" },
  { value: "America/Denver", label: "Mountain Time (US)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Shanghai", label: "Shanghai" },
  { value: "Australia/Sydney", label: "Sydney" },
];

export default function CreatePodcastPage() {
  const { organizationId: _organizationId } = useLoaderData() as CreatePodcastLoaderData;
  const actionData = useActionData() as
    | ActionResult<{ id: string; redirectTo: string }>
    | undefined;
  const navigation = useNavigation();
  const navigate = useNavigate();

  const isSubmitting = navigation.state === "submitting";

  // Navigate to the newly created podcast on success
  useEffect(() => {
    if (actionData?.success && actionData?.data?.redirectTo) {
      toast.success("Podcast created successfully!");
      navigate(actionData.data.redirectTo, { replace: true });
    }
  }, [actionData, navigate]);

  const [name, setName] = useState("");
  const [slugOverride, setSlugOverride] = useState("");
  const [showSlugInput, setShowSlugInput] = useState(false);

  // Detect user's timezone
  const userTimezone = useMemo(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Check if it's in our list
      if (TIMEZONES.some((t) => t.value === tz)) {
        return tz;
      }
      return "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const generatedSlug = useMemo(() => generateSlug(name), [name]);
  const displaySlug = slugOverride || generatedSlug;

  useEffect(() => {
    if (actionData?.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.createPodcastContainer}>
          <div className={styles.backSection}>
            <Link to="/dashboard" className={styles.backLink}>
              &larr; Back to Podcasts
            </Link>
          </div>

          <div className={styles.card}>
            <h1>Create Podcast</h1>
            <p className={styles.description}>
              Set up your new podcast. You can always change these settings later.
            </p>

            <Form method="post" className={styles.form}>
              {/* Podcast Name */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.error : ""}`}
              >
                <label htmlFor="name">Podcast Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="My Awesome Podcast"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                {actionData?.fieldErrors?.name && (
                  <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                )}
              </div>

              {/* Slug */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.error : ""}`}
              >
                <label htmlFor="slug">
                  URL Slug
                  {!showSlugInput && displaySlug && (
                    <button
                      type="button"
                      className={styles.editSlugButton}
                      onClick={() => setShowSlugInput(true)}
                    >
                      Edit
                    </button>
                  )}
                </label>
                {showSlugInput ? (
                  <input
                    id="slug"
                    name="slug"
                    type="text"
                    placeholder="my-awesome-podcast"
                    value={slugOverride}
                    onChange={(e) => setSlugOverride(e.target.value.toLowerCase())}
                    disabled={isSubmitting}
                  />
                ) : (
                  <>
                    <div className={styles.slugPreview}>
                      {displaySlug || (
                        <span className={styles.slugPlaceholder}>auto-generated-from-name</span>
                      )}
                    </div>
                    <input type="hidden" name="slug" value={slugOverride} />
                  </>
                )}
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
                <span className={styles.hint}>
                  This will be used in your podcast's RSS feed URL
                </span>
              </div>

              {/* Description */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.description ? styles.error : ""}`}
              >
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="Tell listeners what your podcast is about..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.description && (
                  <span className={styles.formError}>{actionData.fieldErrors.description}</span>
                )}
              </div>

              {/* Cover Artwork URL - placeholder until S3 is ready */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.coverArtworkUrl ? styles.error : ""}`}
              >
                <label htmlFor="coverArtworkUrl">Cover Artwork URL</label>
                <input
                  id="coverArtworkUrl"
                  name="coverArtworkUrl"
                  type="url"
                  placeholder="https://example.com/artwork.jpg"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.coverArtworkUrl && (
                  <span className={styles.formError}>{actionData.fieldErrors.coverArtworkUrl}</span>
                )}
                <span className={styles.hint}>
                  Recommended: 3000x3000px, square image. Upload feature coming soon.
                </span>
              </div>

              {/* Two-column layout for smaller fields */}
              <div className={styles.twoColumn}>
                {/* Language */}
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.language ? styles.error : ""}`}
                >
                  <label htmlFor="language">Language</label>
                  <select id="language" name="language" defaultValue="en" disabled={isSubmitting}>
                    {PODCAST_LANGUAGES.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.language && (
                    <span className={styles.formError}>{actionData.fieldErrors.language}</span>
                  )}
                </div>

                {/* Category */}
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.category ? styles.error : ""}`}
                >
                  <label htmlFor="category">Category</label>
                  <select id="category" name="category" defaultValue="" disabled={isSubmitting}>
                    <option value="">Select a category</option>
                    {PODCAST_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.category && (
                    <span className={styles.formError}>{actionData.fieldErrors.category}</span>
                  )}
                </div>
              </div>

              <div className={styles.twoColumn}>
                {/* Timezone */}
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.timezone ? styles.error : ""}`}
                >
                  <label htmlFor="timezone">Timezone</label>
                  <select
                    id="timezone"
                    name="timezone"
                    defaultValue={userTimezone}
                    disabled={isSubmitting}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.timezone && (
                    <span className={styles.formError}>{actionData.fieldErrors.timezone}</span>
                  )}
                  <span className={styles.hint}>Used for scheduled episode publishing</span>
                </div>

                {/* Explicit */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" name="explicit" value="true" disabled={isSubmitting} />
                    <span>Contains explicit content</span>
                  </label>
                  <span className={styles.hint}>
                    Mark if your podcast contains adult language or themes
                  </span>
                </div>
              </div>

              {/* Premium Settings Section */}
              <div className={styles.premiumSection}>
                <h3>Monetization</h3>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" name="isPremium" value="true" disabled={isSubmitting} />
                    <span>Enable premium/paid content</span>
                  </label>
                  <span className={styles.hint}>
                    Allow paid subscribers to access exclusive episodes
                  </span>
                </div>
              </div>

              {/* Form Actions */}
              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Creating..."
                >
                  Create Podcast
                </Button>
                <LinkButton to="/dashboard" variant="secondary">
                  Cancel
                </LinkButton>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
